import { createTheme, alpha, PaletteMode, Shadows } from "@mui/material/styles";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
    border: string;
  }
  interface PaletteOptions {
    baseShadow?: string;
    border?: string;
  }
}

const defaultTheme = createTheme();

// The public pages and learning tools share the same warm paper, charcoal and violet.
export const brand = {
  50: "#f3f1fa",
  100: "#e9e5f5",
  200: "#d5cfec",
  300: "#bfb6e4",
  400: "#aaa2df",
  500: "#8178b5",
  600: "#635b9b",
  700: "#514a82",
  800: "#403962",
  900: "#302b49",
  950: "#211e32",
};
export const gray = {
  50: "#fbfaf7",
  100: "#f5f3ee",
  200: "#edeee9",
  300: "#cfcec9",
  400: "#b0b3bd",
  500: "#82858f",
  600: "#5e626c",
  700: "#44464f",
  800: "#222533",
  900: "#1c1d24",
  950: "#16171c",
};

export const green = {
  50: "#ECFDF5",
  100: "#D1FAE5",
  200: "#A7F3D0",
  300: "#6EE7B7",
  400: "#34D399",
  500: "#10B981",
  600: "#059669",
  700: "#047857",
  800: "#065F46",
  900: "#064E3B",
  950: "#022C22",
};

export const orange = {
  50: "#FFF7ED",
  100: "#FFEDD5",
  200: "#FED7AA",
  300: "#FDBA74",
  400: "#FB923C",
  500: "#F97316",
  600: "#EA580C",
  700: "#C2410C",
  800: "#9A3412",
  900: "#7C2D12",
  950: "#431407",
};

export const red = {
  50: "#FEF2F2",
  100: "#FEE2E2",
  200: "#FECACA",
  300: "#FCA5A5",
  400: "#F87171",
  500: "#EF4444",
  600: "#DC2626",
  700: "#B91C1C",
  800: "#991B1B",
  900: "#7F1D1D",
  950: "#450A0A",
};

export const typography = {
  fontFamily: '"Manrope", Arial, sans-serif',
  fontFamilyMono: '"JetBrains Mono", ui-monospace, monospace',
  h1: {
    fontSize: "3rem",
    fontWeight: 500,
    lineHeight: 1.1,
    letterSpacing: "-0.035em",
  },
  h2: {
    fontSize: "2.5rem",
    fontWeight: 500,
    lineHeight: 1.15,
    letterSpacing: "-0.035em",
  },
  h3: {
    fontSize: "1.875rem",
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: "-0.025em",
  },
  h4: {
    fontSize: "1.5rem",
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: "-0.02em",
  },
  h5: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 },
  subtitle1: { fontSize: "1rem", fontWeight: 500 },
  subtitle2: { fontSize: "0.875rem", fontWeight: 600 },
  body1: { fontSize: "1rem", lineHeight: 1.7 },
  body2: { fontSize: "0.875rem", lineHeight: 1.6 },
  caption: { fontSize: "0.8125rem", fontWeight: 400, lineHeight: 1.5 },
  button: { fontWeight: 500, textTransform: "none" as const },
  overline: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: "0.02em",
    textTransform: "none" as const,
  },
};

export const shadows = defaultTheme.shadows.map(() => "none") as Shadows;
export const shape = { borderRadius: 4 };

export const getDesignTokens = (mode: PaletteMode) => {
  const dark = mode === "dark";
  const ink = dark ? gray[200] : gray[800];
  const paper = dark ? gray[950] : gray[100];
  const accent = dark ? brand[400] : brand[600];
  return {
    palette: {
      mode,
      primary: {
        ...brand,
        main: accent,
        light: brand[300],
        dark: brand[700],
        contrastText: dark ? gray[950] : gray[50],
      },
      info: {
        main: accent,
        light: brand[300],
        dark: brand[700],
        contrastText: dark ? gray[950] : gray[50],
      },
      success: {
        main: dark ? "#81c7a6" : "#287452",
        light: "#b5ddc9",
        dark: "#205b41",
        contrastText: dark ? gray[950] : gray[50],
      },
      warning: {
        main: dark ? "#dbb27d" : "#8b5c22",
        light: "#ead0ae",
        dark: "#69451a",
        contrastText: dark ? gray[950] : gray[50],
      },
      error: {
        main: dark ? "#e9a09d" : "#ad4443",
        light: "#f0c1bf",
        dark: "#883534",
        contrastText: dark ? gray[950] : gray[50],
      },
      grey: gray,
      divider: dark ? gray[700] : gray[300],
      border: dark ? gray[700] : gray[300],
      background: { default: paper, paper: dark ? gray[900] : gray[50] },
      text: {
        primary: ink,
        secondary: dark ? gray[400] : gray[600],
        disabled: dark ? "#858891" : "#767982",
      },
      action: {
        hover: alpha(ink, 0.06),
        selected: alpha(accent, dark ? 0.16 : 0.1),
        disabled: dark ? "#858891" : "#767982",
        disabledBackground: alpha(ink, 0.08),
        focus: alpha(accent, 0.14),
      },
    },
    typography,
    shape,
    shadows,
  };
};

export const colorSchemes = {
  light: { palette: getDesignTokens("light").palette },
  dark: { palette: getDesignTokens("dark").palette },
};
